import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { getOrder } from '@services/order.service'
import { updateOrderStatus } from '@services/admin.service'
import './OrderDetail.scss'

const STATUS_OPTIONS = ['pending', 'confirmed', 'processing', 'shipped', 'delivered', 'cancelled']
const PAYMENT_OPTIONS = ['pending', 'paid', 'failed', 'refunded']

function formatDate(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('ro-RO', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

function formatMoney(amount) {
  return new Intl.NumberFormat('ro-RO', { style: 'currency', currency: 'RON' }).format(amount ?? 0)
}

export default function AdminOrderDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [order, setOrder] = useState(null)
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [formStatus, setFormStatus] = useState('')
  const [formPaymentStatus, setFormPaymentStatus] = useState('')
  const [formTracking, setFormTracking] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    getOrder(id)
      .then((res) => {
        const payload = res?.data ?? res
        if (cancelled) return
        setOrder(payload?.order ?? null)
        setItems(payload?.items ?? [])
        if (payload?.order) {
          setFormStatus(payload.order.status ?? '')
          setFormPaymentStatus(payload.order.payment_status ?? '')
          setFormTracking(payload.order.tracking_number ?? '')
        }
      })
      .catch((e) => {
        if (!cancelled) setError(e.response?.data?.message || e.message || 'Eroare la încărcare')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [id])

  const handleUpdateStatus = async (e) => {
    e.preventDefault()
    if (!formStatus && !formPaymentStatus && formTracking === '') return
    setSaving(true)
    try {
      const payload = {}
      if (formStatus) payload.status = formStatus
      if (formPaymentStatus) payload.paymentStatus = formPaymentStatus
      if (formTracking !== undefined) payload.trackingNumber = formTracking.trim() || undefined
      const res = await updateOrderStatus(id, payload)
      const updated = res?.data ?? res
      if (updated) setOrder(updated)
    } catch (e) {
      alert(e.response?.data?.message || e.message || 'Eroare la actualizare')
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <div className="admin-page">Se încarcă...</div>
  if (error) return <div className="admin-page admin-page__error">{error}</div>
  if (!order) return null

  const customerName = [order.customer_first_name, order.customer_last_name].filter(Boolean).join(' ')

  return (
    <div className="admin-page">
      <div className="order-detail-header admin-card">
        <div className="order-detail-header__top">
          <h2 className="order-detail-header__title">{order.order_number}</h2>
          <span className={`admin-badge status-${order.status}`}>{order.status}</span>
          <span className={`admin-badge status-${order.payment_status}`}>{order.payment_status}</span>
        </div>
        <p className="order-detail-header__date">Data: {formatDate(order.created_at)}</p>
      </div>

      <div className="order-detail-grid">
        <div className="admin-card order-detail-card">
          <h3 className="order-detail-card__title">Client</h3>
          <p><strong>Nume:</strong> {customerName}</p>
          <p><strong>Email:</strong> {order.customer_email}</p>
          <p><strong>Telefon:</strong> {order.customer_phone ?? '—'}</p>
        </div>
        <div className="admin-card order-detail-card">
          <h3 className="order-detail-card__title">Livrare</h3>
          <p><strong>Adresă:</strong> {order.shipping_address}</p>
          <p><strong>Oraș:</strong> {order.shipping_city}</p>
          <p><strong>Cod poștal:</strong> {order.shipping_postal_code}</p>
          <p><strong>Județ:</strong> {order.shipping_county ?? '—'}</p>
        </div>
      </div>

      <div className="admin-card">
        <h3 className="order-detail-card__title">Produse</h3>
        <table className="admin-table">
          <thead>
            <tr>
              <th>Produs</th>
              <th>Variant</th>
              <th>Cantitate</th>
              <th>Preț unitar</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            {items.map((row, i) => (
              <tr key={i}>
                <td>{row.product_name}</td>
                <td>{row.variant_name ?? '—'}</td>
                <td>{row.quantity}</td>
                <td>{formatMoney(row.unit_price)}</td>
                <td>{formatMoney(row.total_price)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="order-detail-totals">
          <p><strong>Subtotal:</strong> {formatMoney(order.subtotal)}</p>
          <p><strong>Livrare:</strong> {formatMoney(order.shipping_cost)}</p>
          {Number(order.discount_amount) > 0 && <p><strong>Reducere:</strong> -{formatMoney(order.discount_amount)}</p>}
          <p><strong>Total:</strong> {formatMoney(order.total_amount)}</p>
        </div>
      </div>

      <div className="admin-card order-detail-actions">
        <h3 className="order-detail-card__title">Actualizare status</h3>
        <form onSubmit={handleUpdateStatus} className="order-detail-form">
          <div className="order-detail-form__row">
            <label>
              Status
              <select value={formStatus} onChange={(e) => setFormStatus(e.target.value)}>
                {STATUS_OPTIONS.map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            </label>
            <label>
              Plată
              <select value={formPaymentStatus} onChange={(e) => setFormPaymentStatus(e.target.value)}>
                {PAYMENT_OPTIONS.map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            </label>
            <label>
              AWB / Tracking
              <input type="text" value={formTracking} onChange={(e) => setFormTracking(e.target.value)} placeholder="Număr AWB" />
            </label>
          </div>
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? 'Se salvează...' : 'Actualizează'}
          </button>
        </form>
      </div>

      <p><button type="button" className="btn-ghost" onClick={() => navigate('/admin/orders')}>Înapoi la comenzi</button></p>
    </div>
  )
}
